<template>
  <div v-if="book" class="flex flex-col pt-25 md:pt-50 h-full">
    <div class="text-app-color flex items-center justify-between mb-12 pl-25 md:pl-30">
      <p class="font-Inter text-18 font-semibold truncate pr-5">{{ book.title }}</p>
      <i
        class="cursor-pointer icon-add text-10 mr-20 p-7 transition duration-300 rounded-4"
        :class="isDarkMode ? 'hover:bg-hover' : 'hover:bg-project'"
        @click="createProject"
      />
    </div>

    <div
      id="projects"
      :class="[
        showSidebar ? 'overflow-y-auto' : 'overflow-hidden',

        { 'flex-1': isProjectInstructionsAccepted },
        { 'ml-20': isPoseidonTheme }
      ]"
    >
      <div v-if="projectInstructionsActiveFirstStep" class="absolute w-full h-full z-9999" />
      <Draggable
        :key="filteredProjects.length"
        ref="treeRef"
        :trigger-class="isMobile ? 'test' : 'tree-node'"
        id-key="id"
        parent-id-key="parentId"
        :flat-data="filteredProjects"
        virtualization
        :indent="20"
        :each-droppable="eachDroppable"
        :edge-scroll="true"
        @drop="onDrop"
        @drag="onDragStart"
      >
        <template #default="{node, path, tree}">
          <ProjectItem
            :project="node"
            :level="node.$level"
            :is-project-instruction="projectInstructionsActiveFirstStep"
            :is-project-instructions-accepted="isProjectInstructionsAccepted"
            :book="book"
            :folded="node.$folded"
            @delete="showConfirm"
            @rename="rename"
            @fold="tree.toggleFold(node, path)"
          />
        </template>
      </Draggable>
    </div>

    <div v-if="deletedProjects.length">
      <transition-group name="fade">
        <DeleteNotification
          v-for="(deletedProject, index) in deletedProjects"
          :key="deletedProject"
          :index="index"
          type="Page"
          @close="closeNotification(deletedProject)"
          @restore="restore(deletedProject)"
        />
      </transition-group>
    </div>

    <ConfirmModal
      ref="confirm"
      title="Delete page"
      confirm-btn-title="Delete"
      confirm-btn-class="bg-error"
      @confirm="onDelete"
    >
      <p class="text-14 mt-20 mb-25">Are you sure you want to delete a page with its nested pages?</p>
    </ConfirmModal>
  </div>
</template>

<script>
import ProjectItem from '@/components/projects/ProjectItem'
import DeleteNotification from '@/components/shared/DeleteNotification'
import ConfirmModal from '@/components/confirm/ConfirmModal'
// import { Tree, Draggable, Fold } from 'he-tree-vue'
import Draggable from '@/components/he-tree/Draggable.vue'

import localStorageService from '@/core/local-storage'

import { projectService } from '@/services'

import { defineComponent, ref, computed, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'

export default defineComponent({
  components: {
    ProjectItem,
    DeleteNotification,
    ConfirmModal,
    Draggable
  },

  setup () {
    const { state, getters, commit, dispatch } = useStore()
    const route = useRoute()

    const project = ref(null)
    const confirm = ref(null)

    const book = computed(() => state.book.book)
    const deletedProjects = computed(() => state.project.deletedProjects)
    const showSidebar = computed(() => state.settings.showSidebar)
    const isDarkMode = computed(() => getters.isDarkMode)
    const isMobile = computed(() => getters.isMobile)
    const isProjectInstructionsAccepted = computed(() => state.profile.user.profile.showProjectOnboarding)
    const projectInstructionsActiveStep = computed(() => state.profile.activeStep)
    const projectInstructionsActiveFirstStep = computed(() => !isProjectInstructionsAccepted.value && projectInstructionsActiveStep.value === 1)
    const isPoseidonTheme = computed(() => state.settings.settings.theme === 'Poseidon')

    const treeRef = ref(null)

    const dragNode = ref(null)

    const defaultNestedProjects = localStorageService.getItem('default-nested-projects') || []

    function isFolded (item) {
      return defaultNestedProjects.includes(item.id)
    }

    const projectItemClasses = (item) => {
      const isActive = +route.query?.page === item.id

      if (isProjectInstructionsAccepted.value) {
        return isActive ? 'bg-project' : 'hover:bg-project-hover filter-brightness'
      }
    }

    const computedProjects = computed(() => {
      return book.value.projects.map(project => {
        return {
          ...project,
          $outerClass: projectItemClasses(project),
          $folded: isFolded(project)
        }
      }).sort((a, b) => {
        if (a.order > b.order) return 1
        if (a.order < b.order) return -1
        return 0
      })
    })

    const filteredProjects = computed(() => computedProjects.value)
    let timer = null

    function onDragStart (value) {
      clearTimeout(timer)
      dragNode.value = value.draggingNode
    }

    function onDrop (value) {
      const ids = value.targetTree.nodes.map(item => item.id)

      projectService.order(ids)

      value.draggingNode.parentId = value.draggingNode.$pid || null
      commit('loading', true)

      projectService.edit(value.draggingNode.id, {
        title: value.draggingNode.title,
        parentId: value.draggingNode.$pid || null
      }).then(() => {
        timer = setTimeout(() => {
          commit('loading', true)
          dispatch('getBook', book.value.id)
        }, 500)
      })
    }

    function eachDroppable (data) {
      const dropLevel = dragNode.value.$level
      const hasChildren = dragNode.value.$children?.length > 0

      const firstCondition = dropLevel === 1 && hasChildren && dragNode.value.$children.some(i => i.subProject.length > 0) && data.$level >= 1
      const secondCondition = dropLevel <= 2 && hasChildren && data.$level >= 2

      if (firstCondition || secondCondition || data.$level >= 3) return false
    }

    function rename ({ id, title }) {
      const data = treeRef.value.outputFlatData()
      commit('setBookProjects', data)
    }

    onBeforeUnmount(() => {
      window.removeEventListener('darkModeEvent', {})
    })

    function createProject () {
      dispatch('addProject')
    }

    function restore (id) {
      commit('toggleBookLoading', true)

      dispatch('restoreProject', id)
        .then(_ => (commit('toggleBookLoading', false)))
        .catch(_ => (commit('toggleBookLoading', false)))
    }

    function closeNotification (id) {
      const arr = deletedProjects.value.filter(item => item !== id)
      commit('setDeletedProjects', arr)
    }

    function onDelete () {
      dispatch('deleteProject', project.value.id)
      confirm.value.close()
    }

    function showConfirm (value) {
      if (value.subProject && value.subProject.length) {
        project.value = value
        confirm.value.open()
      } else {
        dispatch('deleteProject', value.id)
      }
    }

    return {
      // refs
      confirm,
      treeRef,
      // computed
      book,
      deletedProjects,
      showSidebar,
      isDarkMode,
      filteredProjects,
      isProjectInstructionsAccepted,
      projectInstructionsActiveFirstStep,
      isPoseidonTheme,
      isMobile,
      // functions
      createProject,
      restore,
      closeNotification,
      onDelete,
      showConfirm,
      onDrop,
      onDragStart,
      rename,
      eachDroppable
    }
  }
})
</script>

<style>
.he-tree .tree-placeholder{
  background: #ddf2f9;
  border: 1px dashed #00d9ff;
  height: 32px;
}

.he-tree .dragging .tree-node:hover{
  background-color: inherit;
}

.filter-brightness:active {
  filter: brightness(0.9);
}
</style>
